/** Client Directory (merged with Xero Connections).
 *
 *  One place to see and act on every client:
 *   • Card lists client name / location, magic-link and management shortcut.
 *   • Xero-connected clients show a live/expired chip based on the token's
 *     expires_at plus one-click token refresh + last-sync timestamp.
 *   • The optional ?xero= query params surface success/error toasts after
 *     the Xero OAuth redirect (previously routed to /admin/connections).
 *
 *  Xero Connections and Report Templates pages have been removed; this page
 *  is the single admin home.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, copyToClipboard, fmtDateUK } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FlashReportActions } from "@/components/FlashReportActions";
import { MailConnectionCard } from "@/components/MailConnectionCard";
import {
  Plus, Copy, ArrowRight, Storefront, Link as LinkIcon, Trash,
  ArrowsClockwise, CheckCircle, XCircle, Warning, LinkSimpleHorizontal,
} from "@phosphor-icons/react";

function fmtTs(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${fmtDateUK(iso)} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [sp] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [refreshing, setRefreshing] = useState({});
  // Tracks sites where a silent refresh failed — flips the row's ↻ icon into a
  // "Reconnect" CTA that starts a fresh Xero OAuth flow for that tenant.
  const [reauthNeeded, setReauthNeeded] = useState({});
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [autoRefreshedOnce, setAutoRefreshedOnce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // /connections requires xero_configured to be true — swallow 4xx quietly
      // so demo installs still render the directory.
      const [cRes, xRes] = await Promise.allSettled([
        api.get("/clients"),
        api.get("/connections"),
      ]);
      setClients(cRes.status === "fulfilled" ? cRes.value.data : []);
      setConns(xRes.status === "fulfilled" ? xRes.value.data : []);
      if (cRes.status === "rejected") toast.error("Could not load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Xero OAuth redirects previously landed on /admin/connections; the flow
    // now returns straight here so we still need to surface success/failure
    // toasts if the redirect includes ?xero=... params.
    if (sp.get("xero") === "connected") {
      toast.success("Xero connected");
      const skipped = Number(sp.get("skipped") || 0);
      if (skipped > 0) {
        toast.info(`${skipped} other organisation${skipped === 1 ? " was" : "s were"} selected in Xero but not linked — one organisation per site.`);
      }
    }
    const err = sp.get("xero_error");
    if (err) {
      // Xero's "access_denied" (a.k.a. TenantConsent status DENIED) fires when
      // the accountant clicks Cancel on the consent page or when the signed-in
      // Xero user lacks permission to grant access to the target org. Surface
      // an actionable message rather than the raw error code.
      const desc = sp.get("desc") || "";
      const isDenied = err === "access_denied" || /denied/i.test(err) || /denied/i.test(desc);
      if (isDenied) {
        toast.error("Xero access denied", {
          description: "You (or the connected Xero user) declined to grant FLASH access. Reconnect and choose \"Allow access\" — and make sure you're signed in to Xero as a user who can see the target organisation.",
          duration: 12000,
        });
      } else {
        toast.error(`Xero: ${err}`, { description: desc, duration: 10000 });
      }
    }
  }, [load, sp]);

  // Look up the /connections row for a given client id — undefined if the
  // client isn't Xero-connected or the endpoint returned 4xx.
  const connByClient = useMemo(() => {
    const map = new Map();
    for (const c of conns) map.set(c.site_id, c);
    return map;
  }, [conns]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", { name, location });
      toast.success(`${name} added`);
      setName(""); setLocation(""); setOpen(false);
      load();
    } catch {
      toast.error("Create failed");
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/c/${token}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success("Magic link copied to clipboard");
    else toast.message("Copy not allowed here", { description: url });
  };

  // `regen` (regenerate magic link) was retired from this page — it now lives
  // only on the Client Management screen to avoid accidental link rotation.

  const remove = async (id, n) => {
    if (!window.confirm(`Remove client "${n}"?`)) return;
    await api.delete(`/clients/${id}`);
    toast.success("Removed");
    load();
  };

  const refreshTokens = async (siteId) => {
    setRefreshing((s) => ({ ...s, [siteId]: true }));
    try {
      const { data } = await api.post(`/connections/${siteId}/refresh`);
      toast.success(`Xero refreshed · expires ${fmtDateUK(data.expires_at)}`);
      setReauthNeeded((s) => { const n = { ...s }; delete n[siteId]; return n; });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Refresh failed";
      const isReauth = /reauth|invalid_grant|revoked|expired/i.test(detail);
      if (isReauth) setReauthNeeded((s) => ({ ...s, [siteId]: true }));
      toast.error(detail);
    } finally {
      setRefreshing((s) => ({ ...s, [siteId]: false }));
    }
  };

  /** Open the standard Xero OAuth consent flow for a specific tenant's parent
   *  client. Xero's consent screen supports selecting multiple organisations
   *  in a single flow, so an accountant with several dead tokens can re-approve
   *  them all with one "Allow" click without visiting each row.
   */
  const reconnectXero = useCallback(async (parentClientId) => {
    if (!parentClientId) { toast.error("Missing parent client id"); return; }
    try {
      const { data } = await api.get(`/clients/${parentClientId}/xero/authorize`);
      if (data?.authorize_url) window.location.href = data.authorize_url;
      else toast.error("Could not start Xero OAuth");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconnect failed to start");
    }
  }, []);

  /** Bulk-refresh every expired Xero connection in parallel. Any site whose
   *  refresh token is genuinely dead surfaces a "Reconnect" CTA on the row.
   */
  const refreshAllExpired = useCallback(async (opts = { silent: false }) => {
    const expired = (conns || []).filter((c) => c.status === "Expired");
    if (expired.length === 0) {
      if (!opts.silent) toast.info("Nothing to refresh · all tokens are live");
      return;
    }
    setBulkRefreshing(true);
    setRefreshing((s) => {
      const next = { ...s };
      for (const c of expired) next[c.site_id] = true;
      return next;
    });
    const results = await Promise.allSettled(
      expired.map((c) => api.post(`/connections/${c.site_id}/refresh`).then(() => c).catch((e) => {
        const err = new Error(e?.response?.data?.detail || "refresh_failed");
        err.site_id = c.site_id;
        err.parent_client_id = c.parent_client_id;
        err.detail = e?.response?.data?.detail || "refresh_failed";
        throw err;
      }))
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results
      .filter((r) => r.status === "rejected")
      .map((r) => r.reason);
    if (failed.length > 0) {
      setReauthNeeded((s) => {
        const next = { ...s };
        for (const f of failed) if (f.site_id) next[f.site_id] = true;
        return next;
      });
    }
    setRefreshing({});
    setBulkRefreshing(false);
    await load();
    if (!opts.silent) {
      if (failed.length === 0) toast.success(`Refreshed ${ok} Xero connection${ok === 1 ? "" : "s"}`);
      else toast.warning(`${ok} refreshed · ${failed.length} need reconnect`, {
        description: "Click the amber Reconnect button on each affected row.",
        duration: 8000,
      });
    }
    return { ok, failed };
  }, [conns, load]);

  // Auto-refresh once per mount if any Xero token appears Expired at page load.
  // Runs silently — the vast majority of "Expired" chips are simply access
  // tokens that haven't been rotated yet by the scheduler, and this heals them
  // in a single background pass so the admin rarely sees a Reauth chip.
  useEffect(() => {
    if (autoRefreshedOnce || loading || conns.length === 0) return;
    const anyExpired = conns.some((c) => c.status === "Expired");
    if (!anyExpired) return;
    setAutoRefreshedOnce(true);
    refreshAllExpired({ silent: true });
  }, [conns, loading, autoRefreshedOnce, refreshAllExpired]);

  const expiredCount = useMemo(() => conns.filter((c) => c.status === "Expired").length, [conns]);
  // First parent client id we can pump into `xero/authorize` for a top-level
  // "Reconnect Xero" button in the header. Prefer an Expired tenant so the
  // consent flow lands on the right org selector; otherwise fall back to any
  // Xero-connected client (accountant wants to add / re-approve orgs).
  const reconnectAnchorId = useMemo(() => {
    const expired = conns.find((c) => c.status === "Expired" && c.parent_client_id);
    if (expired) return expired.parent_client_id;
    const any = conns.find((c) => c.parent_client_id);
    if (any) return any.parent_client_id;
    return clients[0]?.id || null;
  }, [conns, clients]);

  return (
    <AppShell
      subtitle="Practice"
      title="Client Directory"
      actions={
        <div className="flex items-center gap-2">
          {expiredCount > 0 && (
            <Button
              onClick={() => refreshAllExpired()}
              disabled={bulkRefreshing}
              data-testid="refresh-all-xero-btn"
              variant="outline"
              className="rounded-md h-11 px-4 border-[#E2E8F0] text-[#0F172A] text-xs"
              title="Refresh every Xero connection whose access token has expired"
            >
              <ArrowsClockwise size={14} className={`mr-2 ${bulkRefreshing ? "animate-spin" : ""}`} />
              Refresh all ({expiredCount})
            </Button>
          )}
          {reconnectAnchorId && (
            <Button
              onClick={() => reconnectXero(reconnectAnchorId)}
              data-testid="reconnect-xero-header-btn"
              variant="outline"
              className="rounded-md h-11 px-4 border-amber-300 text-amber-800 hover:bg-amber-50 text-xs"
              title="Start a fresh Xero OAuth flow — Xero's consent screen lets you re-approve every organisation in one click"
            >
              <LinkSimpleHorizontal size={14} className="mr-2" />
              Reconnect Xero
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md h-11 px-5">
                <Plus size={16} weight="bold" className="mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-md" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight">New Restaurant Client</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2" data-testid="create-client-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Restaurant name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="client-name-input" className="rounded-md h-11" placeholder="e.g. Lyle's Shoreditch" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Location (optional)</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="client-location-input" className="rounded-md h-11" placeholder="London EC2" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="submit-client-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <section className="mb-6 bg-white border border-[#E2E8F0] rounded-md card-fade px-6 py-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="mail-settings-panel">
        <div className="md:w-72 shrink-0">
          <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium">Flash Report emails</p>
          <p className="text-xs text-[#64748B] mt-1">One mailbox sends reports for every client.</p>
        </div>
        <div className="flex-1 max-w-2xl"><MailConnectionCard /></div>
      </section>
      {loading ? (
        <div className="text-sm text-[#475569]">Loading clients...</div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-md p-16 text-center">
          <Storefront size={40} weight="duotone" className="mx-auto text-[#94A3B8] mb-4" />
          <p className="font-display text-2xl tracking-tight mb-2">No clients yet</p>
          <p className="text-sm text-[#475569] mb-6">Add your first restaurant client to generate their unique dashboard link.</p>
          <Button onClick={() => setOpen(true)} className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md">
            <Plus size={16} weight="bold" className="mr-2" /> Add your first client
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
        <XeroTenantsPanel conns={conns} />
        <div className="space-y-3" data-testid="clients-grid">
          {clients.map((c) => {
            const conn = connByClient.get(c.id);
            const status = conn?.status;
            return (
              <div key={c.id} className="card-fade bg-white border border-[#E2E8F0] rounded-md p-5 flex items-center gap-5" data-testid={`client-card-${c.id}`}>
                {/* Left: name + location */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl tracking-tight truncate">{c.name}</h3>
                    {c.xero_connected ? (
                      status === "Expired" ? (
                        <span data-testid={`xero-chip-${c.id}`} className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded font-medium bg-rose-50 text-rose-700">
                          <XCircle size={11} weight="fill" /> Reauth
                        </span>
                      ) : (
                        <span data-testid={`xero-chip-${c.id}`} className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded font-medium bg-emerald-50 text-emerald-700">
                          <CheckCircle size={11} weight="fill" /> Xero Live
                        </span>
                      )
                    ) : (
                      <span data-testid={`xero-chip-${c.id}`} className="shrink-0 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded font-medium bg-stone-100 text-stone-600">
                        Demo Data
                      </span>
                    )}
                  </div>
                  {c.location && <p className="text-xs text-[#475569] font-mono mt-0.5">{c.location}</p>}
                </div>

                {/* Middle: Xero tenant + last sync (only for Xero-connected sites) */}
                {c.xero_connected && conn ? (
                  <div className="hidden md:block min-w-0 flex-1 text-xs" data-testid={`xero-strip-${c.id}`}>
                    <div className="flex items-baseline gap-2">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#475569] font-mono shrink-0">Xero</p>
                      {conn.tenant_name && (
                        <p className="text-[#0F172A] truncate" title={conn.tenant_name}>{conn.tenant_name}</p>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-[#94A3B8] mt-0.5">
                      {conn.expires_at && <>expires {fmtDateUK(conn.expires_at)}</>}
                      {conn.last_sync_at && <> · synced {fmtTs(conn.last_sync_at)}</>}
                      {conn.last_sync_count != null && <> · {conn.last_sync_count} lines</>}
                    </div>
                  </div>
                ) : (
                  <div className="hidden md:block flex-1" />
                )}

                {/* Right: magic link + actions */}
                <div className="text-xs text-[#94A3B8] font-mono flex items-center gap-1.5 shrink-0">
                  <LinkIcon size={12} />
                  <span className="hidden lg:inline">/c/{c.magic_token.slice(0, 10)}…</span>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <FlashReportActions clientId={c.id} clientName={c.name} testid={`flash-report-${c.id}`} />
                  <Button size="sm" variant="outline" onClick={() => copyLink(c.magic_token)} data-testid={`copy-link-${c.id}`} className="rounded-md border-[#E2E8F0] text-xs" title="Copy magic link">
                    <Copy size={13} />
                  </Button>
                  {/* "Regenerate magic link" moved to Client Management to
                      avoid accidental link rotation from the directory row. */}
                  {c.xero_connected && (
                    reauthNeeded[c.id] ? (
                      <Button
                        size="sm"
                        onClick={() => reconnectXero(conn?.parent_client_id || c.parent_client_id || c.id)}
                        data-testid={`reconnect-xero-${c.id}`}
                        className="rounded-md text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
                        title="Refresh token dead — start a Xero OAuth re-authorisation for this org"
                      >
                        <Warning size={13} className="mr-1" /> Reconnect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => refreshTokens(c.id)}
                        disabled={!!refreshing[c.id]}
                        data-testid={`refresh-xero-${c.id}`}
                        className="rounded-md border-[#E2E8F0] text-xs"
                        title="Refresh Xero tokens"
                      >
                        <ArrowsClockwise size={13} className={`${refreshing[c.id] ? "animate-spin" : ""}`} />
                      </Button>
                    )
                  )}
                  <Link to={`/admin/clients/${c.id}`}>
                    <Button size="sm" data-testid={`manage-client-${c.id}`} className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md text-xs">
                      Manage <ArrowRight size={12} className="ml-1.5" />
                    </Button>
                  </Link>
                  <button onClick={() => remove(c.id, c.name)} data-testid={`delete-client-${c.id}`} className="text-[#94A3B8] hover:text-rose-700 ml-1" title="Remove client">
                    <Trash size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
    </AppShell>
  );
}

/** Dedicated Xero organisations panel — surfaces every tenant granted access
 *  to this admin across all clients, independent of the parent/child client
 *  hierarchy. Xero's /connections can hand back multiple tenants under a
 *  single OAuth grant, so accountants can lose track of how many orgs are
 *  actually feeding data into FLASH — this panel makes that visible.
 */
function XeroTenantsPanel({ conns }) {
  const active = (conns || []).filter((c) => c.tenant_id);
  const activeCount = active.filter((c) => c.status !== "Expired").length;
  if (active.length === 0) return null;

  return (
    <section className="mb-6 bg-white border border-[#E2E8F0] rounded-md card-fade overflow-hidden" data-testid="xero-tenants-panel">
      <div className="px-5 py-4 border-b border-[#EEEEEE] flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium">Xero · Connected Organisations</p>
          <h2 className="font-display text-xl tracking-tight mt-0.5">{active.length} tenant{active.length === 1 ? "" : "s"} linked to this practice</h2>
        </div>
        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#94A3B8]">
          {activeCount} active · {active.length - activeCount} expired
        </p>
      </div>
      <table className="w-full text-xs" data-testid="xero-tenants-table">
        <thead>
          <tr className="border-b border-[#EEEEEE] text-left">
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium px-5 py-2.5">Xero Org</th>
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium py-2.5">Client Row</th>
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium py-2.5">Status</th>
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium py-2.5">Connected</th>
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium py-2.5">Scopes</th>
            <th className="text-[11px] uppercase tracking-wider text-[#475569] font-medium text-right pr-5 py-2.5">Dashboard</th>
          </tr>
        </thead>
        <tbody>
          {active.map((c) => {
            const isActive = c.status !== "Expired";
            const scopeList = (c.scope || "").split(/\s+/).filter(Boolean);
            const short = scopeList
              .filter((s) => s.startsWith("accounting."))
              .map((s) => s.replace("accounting.", "").replace(".read", ""))
              .join(" · ");
            const dashUrl = c.magic_token ? `/c/${c.magic_token}` : null;
            return (
              <tr key={c.site_id} className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5]" data-testid={`xero-tenant-row-${c.tenant_id}`}>
                <td className="px-5 py-3 font-medium text-[#0F172A]">
                  {c.tenant_name || c.site_name}
                  {c.tenant_id && (
                    <p className="text-[11px] font-mono text-[#94A3B8] mt-0.5">tenant · {c.tenant_id.slice(0, 8)}…</p>
                  )}
                </td>
                <td className="py-3 text-[#475569]">
                  {c.parent_client_name || c.site_name}
                  {c.parent_client_id && c.parent_client_id !== c.site_id && (
                    <p className="text-[11px] font-mono text-[#94A3B8] mt-0.5">sub-site: {c.site_name}</p>
                  )}
                </td>
                <td className="py-3">
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded font-medium bg-emerald-50 text-emerald-700">
                      <CheckCircle size={11} weight="fill" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded font-medium bg-rose-50 text-rose-700">
                      <XCircle size={11} weight="fill" /> Reauth
                    </span>
                  )}
                </td>
                <td className="py-3 font-mono text-[11px] text-[#475569]">
                  {c.connected_at ? fmtDateUK(c.connected_at) : "—"}
                </td>
                <td className="py-3 font-mono text-[11px] text-[#475569] max-w-[280px] truncate" title={c.scope || ""}>
                  {short || "—"}
                </td>
                <td className="py-3 pr-5 text-right">
                  {dashUrl ? (
                    <Link to={dashUrl} target="_blank" data-testid={`xero-tenant-open-${c.tenant_id}`}>
                      <Button size="sm" variant="outline" className="rounded-md border-[#E2E8F0] text-xs">
                        Open <ArrowRight size={11} className="ml-1" />
                      </Button>
                    </Link>
                  ) : (
                    <span className="text-[11px] font-mono text-[#94A3B8]">no magic link</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
